<template>
  <div class="search-order">
    <div class="el-search vue-top-padding radius">
      <vtm-filter-bar
        ref="fb"
        :fields="fields"
        :search="search"
        :presets="presets"
        :extra-keys="['payState', 'payType', 'payStates', 'payTypes', 'sort', 'order', 'machinePicks']"
        :date-time="dateTime"
        storage-key="orders"
        @submit="onSubmit"
        @reset="resetSearch"
      >
        <template #lead>
          <el-button
            class="so-export"
            size="small"
            icon="el-icon-printer"
            :loading="exportLoading"
            @click="exportOrder"
            >{{ $t("common.export") }}</el-button
          >
          <!-- 页面附加按钮（如交易页的列设置） -->
          <slot name="extra"></slot>

          <el-date-picker
            class="so-date"
            size="small"
            v-model="dateTime"
            type="datetimerange"
            :picker-options="pickerOptions"
            :default-time="['00:00:00', '23:59:59']"
            range-separator="-"
            :start-placeholder="$t('searchOrder.start')"
            :end-placeholder="$t('searchOrder.end')"
            @change="onSubmit"
          >
          </el-date-picker>
        </template>

        <template #filters>
          <el-select
            class="so-select so-select--machine"
            size="small"
            v-model="search.machinePicks"
            multiple
            filterable
            remote
            clearable
            :no-data-text="$t('common.noData')"
            :no-match-text="$t('common.noData')"
            :remote-method="handleMachineFilter"
            :loading="machineLoading"
            :placeholder="$t('searchOrder.equipmentName')"
            @focus="ensureMachineOptions"
            @visible-change="onMachineDropdown"
            @change="onSubmit"
          >
            <el-option
              v-for="item in machineOptions"
              :key="item.equipmentNo"
              :label="machineOptionLabel(item)"
              :value="item.equipmentNo"
            ></el-option>
          </el-select>
          <el-button
            v-if="search.machinePicks && search.machinePicks.length"
            class="so-select-clear"
            size="small"
            type="text"
            icon="el-icon-close"
            @click="clearMachines"
            >{{ $t("common.clear") }}</el-button
          >

          <el-select
            class="so-select"
            size="small"
            v-model="search.payStates"
            multiple
            collapse-tags
            :no-data-text="$t('common.noData')"
            :no-match-text="$t('common.noData')"
            clearable
            :placeholder="$t('searchOrder.payState')"
            @change="onSubmit"
          >
            <el-option v-for="item in payStateList" :key="item.id" :label="item.label" :value="item.value"> </el-option>
          </el-select>

          <el-select
            class="so-select"
            size="small"
            v-model="search.payTypes"
            multiple
            collapse-tags
            :no-data-text="$t('common.noData')"
            :no-match-text="$t('common.noData')"
            clearable
            :placeholder="$t('order.payType')"
            @change="onSubmit"
          >
            <el-option v-for="item in payTypeList" :key="item.id" :label="item.label" :value="item.value"> </el-option>
          </el-select>
        </template>
      </vtm-filter-bar>
    </div>
  </div>
</template>

<script>
  import VtmFilterBar from "@/components/common/VtmFilterBar";
  const Cookies = require("js-cookie");

  // 只有超管能看出厂机器列表（equipment/page），机器下拉也搜那里；客服角色上线后再加 "6"
  const FACTORY_SEARCH_ROLES = ["0"];

  // payState：0 待支付 · 1 已支付 · 2 已接单 · 3 已出货 · -1 待退款 · -2 已退款 · -3 已失效 · -4 已取消
  // 与字典 gp_pay_state 的取值类型一致（字符串），否则多选框找不到选项、只显示原始数字
  const PAY_STATE_DELIVERED = "3";
  const PAY_STATE_REFUNDED = "-2";

  export default {
    name: "SearchOrder",
    components: { VtmFilterBar },
    props: {
      search: Object,
      dateTime: Array,
      payStateList: Array,
      payTypeList: Array
    },
    data() {
      return {
        pickerOptions: {
          shortcuts: [
            {
              text: this.$t("TimeControl.week"),
              onClick(picker) {
                const end = new Date();
                end.setHours(23, 59, 59, 999);
                const start = new Date();
                start.setTime(start.getTime() - 3600 * 1000 * 24 * 7);
                picker.$emit("pick", [start, end]);
              }
            },
            {
              text: this.$t("TimeControl.month"),
              onClick(picker) {
                const end = new Date();
                end.setHours(23, 59, 59, 999);
                const start = new Date();
                start.setTime(start.getTime() - 3600 * 1000 * 24 * 30);
                picker.$emit("pick", [start, end]);
              }
            },
            {
              text: this.$t("TimeControl.year"),
              onClick(picker) {
                const end = new Date();
                end.setHours(23, 59, 59, 999);
                const start = new Date();
                start.setTime(start.getTime() - 3600 * 1000 * 24 * 90);
                picker.$emit("pick", [start, end]);
              }
            }
          ]
        },
        exportLoading: false,
        // 按机器名搜索（服务端 userEquipment/select），选中后提交的是机器号
        machineOptions: [],
        // Default page (no keyword). Kept so a number search still finds machines already loaded
        // when the server search only matches the name field.
        machineDefaults: [],
        machineLoading: false,
        machineDebounce: null,
        // 只采用最后一次搜索的结果，慢的旧请求回来不覆盖新结果
        machineRequestSeq: 0,
        machineQuery: ""
      };
    },
    computed: {
      // 可叠加检索的字段（映射到后端 order/page 参数）
      fields() {
        return [
          { value: "orderNumber", label: this.$t("searchOrder.orderNumber") },
          { value: "userName", label: this.$t("searchOrder.userName") },
          // 机器号可用逗号一次输多个；和下拉选中的机器在提交时合并成 equipmentNoList
          { value: "equipmentNo", label: this.$t("searchOrder.equipmentNo"), list: "typedNoList" },
          { value: "goodsName", label: this.$t("order.goodsName") }
        ];
      },
      // 运营最常用的报表口径
      presets() {
        return [
          {
            id: "deliveredThisMonth",
            name: this.$t("searchOrder.presetDeliveredThisMonth"),
            set: { payStates: [PAY_STATE_DELIVERED] },
            range: "thisMonth"
          },
          {
            id: "deliveredLastMonth",
            name: this.$t("searchOrder.presetDeliveredLastMonth"),
            set: { payStates: [PAY_STATE_DELIVERED] },
            range: "lastMonth"
          },
          {
            id: "refunded30",
            name: this.$t("searchOrder.presetRefunded30"),
            set: { payStates: [PAY_STATE_REFUNDED] },
            range: "last30"
          }
        ];
      }
    },
    methods: {
      // 机器名下拉：首次聚焦时加载一批，输入时按名称/编号远程搜索
      ensureMachineOptions() {
        if (!this.machineOptions.length) {
          this.loadMachines("");
        }
      },
      clearMachines() {
        this.search.machinePicks = [];
        this.onSubmit();
      },
      // 重新打开下拉时回到默认列表，而不是上一次的搜索结果
      onMachineDropdown(open) {
        if (open && this.machineQuery) {
          this.loadMachines("");
        }
      },
      // 下拉选中的机器 + 输入框里的机器号 → 后端的 equipmentNoList（有它时后端忽略单个 equipmentNo）
      buildMachineList() {
        const picks = this.search.machinePicks || [];
        let typed = this.search.typedNoList || [];
        if (!typed.length && picks.length && this.search.equipmentNo) {
          typed = [String(this.search.equipmentNo).trim()];
        }
        this.search.equipmentNoList = Array.from(new Set(picks.concat(typed).filter(Boolean)));
      },
      handleMachineFilter(query) {
        clearTimeout(this.machineDebounce);
        this.machineDebounce = setTimeout(() => this.loadMachines(query), 300);
      },
      // userEquipment/select records are { equipmentNo, name }. Some payloads also include equipmentName.
      machineOptionLabel(item) {
        if (!item) return "";
        if (item.name) return item.name;
        if (item.equipmentName) return item.equipmentName;
        return item.equipmentNo || "";
      },
      machineMatchesQuery(item, query) {
        const q = String(query || "").trim().toLowerCase();
        if (!q) return true;
        if (!item) return false;
        return [item.name, item.equipmentName, item.equipmentNo].some(field =>
          String(field || "").toLowerCase().includes(q)
        );
      },
      loadMachines(searchName) {
        const seq = ++this.machineRequestSeq;
        this.machineQuery = searchName;
        this.machineLoading = true;
        const query = String(searchName || "").trim();
        const requests = [
          this.$api.request(this.$url.UserMachine.listUserMachineSelect, this.$method.get, {
            searchName,
            pageIndex: 1,
            pageSize: 50
          })
        ];
        // 已从客户账号移除（未分配）但有历史交易的机器不在 userEquipment/select 里：超管/客服再搜一次出厂机器列表
        if (query && FACTORY_SEARCH_ROLES.includes(String(Cookies.get("roleId")))) {
          requests.push(
            this.$api
              .request(this.$url.Machine.page, this.$method.get, {
                [/^\d+$/.test(query) ? "equipmentNo" : "equipmentName"]: query,
                pageIndex: 1,
                pageSize: 50
              })
              .catch(() => null)
          );
        }
        Promise.all(requests)
          .then(([res, factory]) => {
            if (seq === this.machineRequestSeq && res && res.code === 0 && res.data) {
              const factoryRecords = factory && factory.code === 0 && factory.data ? factory.data.records || [] : [];
              const records = (res.data.records || []).concat(factoryRecords);
              if (!query) {
                this.machineDefaults = records;
                this.machineOptions = records;
              } else {
                const seen = new Set();
                this.machineOptions = records.concat(this.machineDefaults || []).filter(item => {
                  if (!this.machineMatchesQuery(item, query)) return false;
                  const key = item && item.equipmentNo != null ? String(item.equipmentNo) : "";
                  if (key && seen.has(key)) return false;
                  if (key) seen.add(key);
                  return true;
                });
              }
            }
          })
          .finally(() => {
            if (seq === this.machineRequestSeq) {
              this.machineLoading = false;
            }
          });
      },
      onSubmit() {
        // 日期区间同步进检索条件
        if (this.$refs.fb) this.$refs.fb.syncFilters();
        this.buildMachineList();
        if (this.dateTime && this.dateTime.length > 0) {
          this.search.startTime = this.$timeUtil.formatTime(this.dateTime[0]);
          this.search.endTime = this.$timeUtil.formatTime(this.dateTime[1]);
        } else {
          this.search.startTime = "";
          this.search.endTime = "";
        }
        this.$emit("onSubmit", this.search);
      },
      resetSearch() {
        // 多选字段清成 []（清成 "" 会让后端解析失败，导出报错）
        Object.keys(this.search).forEach(k => {
          this.search[k] = Array.isArray(this.search[k]) ? [] : "";
        });
        // 日期回到默认近 7 天，避免选择器显示有区间而实际查的是全部时间
        if (this.dateTime) {
          const end = new Date();
          const start = new Date(end.getTime() - 7 * 24 * 60 * 60 * 1000);
          this.dateTime.splice(0, this.dateTime.length, start, end);
        }
        this.onSubmit();
      },
      exportOrder() {
        // 导出与列表同条件（含所有生效标签）
        if (this.$refs.fb) this.$refs.fb.syncFilters();
        this.buildMachineList();
        this.$confirm(this.$t("searchOrder.exportConfirm"), this.$t("common.export"), {
          confirmButtonText: this.$t("common.definite"),
          cancelButtonText: this.$t("common.cancel")
        })
          .then(() => {
            this.exportLoading = true;
            this.$api.download(this.$url.Order.export, this.search).finally(() => {
              this.exportLoading = false;
            });
          })
          .catch(err => {
            if (err.msg) {
              this.$message.error(err.msg);
            }
          });
      }
    }
  };
</script>

<style scoped lang="scss">
  .so-export {
    flex-shrink: 0;
  }

  .so-date {
    flex-shrink: 0;
    width: 360px !important;
  }

  .so-select {
    flex-shrink: 0;
    width: 150px;
  }

  // 机器名下拉：和其他筛选项同行，只是比 150px 宽一些
  .so-select--machine {
    width: 240px;
  }

  .so-select-clear {
    flex-shrink: 0;
  }

  .search-order .el-search {
    overflow: visible;
  }
</style>
